import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  AlertTriangle,
  ShieldAlert,
  UserX,
  MapPin,
  Locate,
  Clock,
  Send,
  X,
  Loader2,
  ChevronRight,
  Info,
  Phone,
  Bell,
  Flag,
  CheckCircle
} from "lucide-react";

const UrgentAlertsPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [canSend, setCanSend] = useState(null);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [showModal, setShowModal] = useState(null); // 'vol' or 'disparition'
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    alert_type: "",
    sub_type: "",
    latitude: null,
    longitude: null,
    address: "",
    description: "",
    physical_description: ""
  });
  const [locating, setLocating] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const checkCanSend = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/urgent-alerts/can-send`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanSend(response.data);
    } catch (error) {
      setCanSend({ can_send: false, reason: "Erreur de vérification" });
    }
  }, [token]);

  const fetchActiveAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/urgent-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  }, [token]);

  // Check if user can send alerts
  useEffect(() => {
    if (token) {
      checkCanSend();
      fetchActiveAlerts();
    }
  }, [token, checkCanSend, fetchActiveAlerts]);

  const startAlert = (type) => {
    setFormData({
      alert_type: type,
      sub_type: "",
      latitude: null,
      longitude: null,
      address: "",
      description: "",
      physical_description: ""
    });
    setStep(1);
    setSlideProgress(0);
    setShowModal(type);
  };

  const getLocation = () => {
    setLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
          
          // Reverse geocode
          try {
            const response = await axios.get(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`,
              { headers: { 'Accept-Language': 'fr' } }
            );
            if (response.data?.display_name) {
              const addr = response.data.display_name.split(',').slice(0, 3).join(',');
              setFormData(prev => ({ ...prev, address: addr }));
            }
          } catch (e) {
            console.error("Geocoding error:", e);
          }
          setLocating(false);
          toast.success("Position détectée");
        },
        () => {
          setLocating(false);
          toast.error("Impossible de détecter la position. Entrez une adresse manuellement.");
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocating(false);
      toast.error("Géolocalisation non supportée");
    }
  };

  const handleSlideConfirm = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const progress = Math.min(Math.max(x / rect.width, 0), 1);
    setSlideProgress(progress);
    
    if (progress >= 0.9) {
      submitAlert();
    }
  };

  const submitAlert = async () => {
    if (!formData.latitude || !formData.longitude) {
      toast.error("Position requise");
      return;
    }
    if (!formData.sub_type) {
      toast.error("Sélectionnez un type");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/urgent-alerts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowModal(null);
      setStep(1);
      setSlideProgress(0);
      fetchActiveAlerts();
      checkCanSend();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const reportAlert = async (alertId) => {
    try {
      await axios.post(`${API}/urgent-alerts/${alertId}/report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Signalement enregistré");
      fetchActiveAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000);
    if (diff < 1) return "À l'instant";
    if (diff < 60) return `Il y a ${diff} min`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `Il y a ${hours}h`;
    return `Il y a ${Math.floor(hours / 24)}j`;
  };

  const SUBTYPES = {
    vol: [
      { id: "agression", label: "Agression", icon: "🔴" },
      { id: "vol_arrache", label: "Vol à l'arraché", icon: "🎒" },
      { id: "cambriolage", label: "Cambriolage", icon: "🏠" }
    ],
    disparition: [
      { id: "enfant", label: "Enfant", icon: "👶" },
      { id: "personne_agee", label: "Personne âgée", icon: "👴" },
      { id: "personne_vulnerable", label: "Personne vulnérable", icon: "🧑" },
      { id: "animal", label: "Animal de compagnie", icon: "🐕" }
    ]
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Alertes Urgentes
          </h1>
          <p className="text-slate-500 text-sm">
            Signalez un incident pour alerter votre communauté
          </p>
        </div>

        {/* Alert Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Vol Button */}
          <button
            onClick={() => canSend?.can_send && startAlert("vol")}
            disabled={!canSend?.can_send}
            className={`relative overflow-hidden p-6 rounded-2xl border-2 transition-all ${
              canSend?.can_send
                ? "bg-red-50 border-red-200 hover:border-red-400 hover:shadow-lg cursor-pointer"
                : "bg-slate-100 border-slate-200 cursor-not-allowed opacity-60"
            }`}
            data-testid="alert-vol-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <ShieldAlert className="text-white" size={28} />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-bold text-red-700">Signaler un VOL</h3>
                <p className="text-sm text-red-600/70">Agression, vol à l'arraché, cambriolage</p>
              </div>
            </div>
            {canSend?.can_send && (
              <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            )}
          </button>

          {/* Disparition Button */}
          <button
            onClick={() => canSend?.can_send && startAlert("disparition")}
            disabled={!canSend?.can_send}
            className={`relative overflow-hidden p-6 rounded-2xl border-2 transition-all ${
              canSend?.can_send
                ? "bg-orange-50 border-orange-200 hover:border-orange-400 hover:shadow-lg cursor-pointer"
                : "bg-slate-100 border-slate-200 cursor-not-allowed opacity-60"
            }`}
            data-testid="alert-disparition-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <UserX className="text-white" size={28} />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-bold text-orange-700">Signaler une DISPARITION</h3>
                <p className="text-sm text-orange-600/70">Enfant, personne âgée, animal de compagnie</p>
              </div>
            </div>
            {canSend?.can_send && (
              <div className="absolute top-2 right-2 w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
            )}
          </button>
        </div>

        {/* Status / Limitations */}
        {canSend && !canSend.can_send && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800">Alertes temporairement indisponibles</p>
                  <p className="text-sm text-amber-700">{canSend.reason}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {canSend?.can_send && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <CheckCircle size={16} className="text-green-500" />
            <span>Vous pouvez envoyer {canSend.remaining_alerts} alerte(s) cette heure</span>
          </div>
        )}

        {/* How it works */}
        <Card className="guardian-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell size={18} className="text-[#FF3366]" />
              Comment ça fonctionne ?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#FF3366] text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
              <p className="text-sm text-slate-600">Confirmez votre position GPS ou entrez une adresse</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#FF3366] text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
              <p className="text-sm text-slate-600">Décrivez brièvement la situation</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#FF3366] text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
              <div className="text-sm text-slate-600">
                <p><strong>Notification push</strong> envoyée à tous les utilisateurs dans un rayon de 5km</p>
                <p><strong>SMS</strong> envoyé aux numéros vérifiés dans la zone</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <div>
          <h2 className="text-lg font-bold text-slate-800 mb-3">Alertes actives près de vous</h2>
          {activeAlerts.length === 0 ? (
            <Card className="guardian-card">
              <CardContent className="p-6 text-center">
                <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-slate-500">Aucune alerte active dans votre zone</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {activeAlerts.map((alert) => (
                <Card key={alert.id} className={`guardian-card border-l-4 ${
                  alert.alert_type === "vol" ? "border-l-red-500" : "border-l-orange-500"
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          alert.alert_type === "vol" ? "bg-red-100" : "bg-orange-100"
                        }`}>
                          {alert.alert_type === "vol" ? (
                            <ShieldAlert className="text-red-600" size={20} />
                          ) : (
                            <UserX className="text-orange-600" size={20} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className={alert.alert_type === "vol" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}>
                              {alert.alert_type === "vol" ? "VOL" : "DISPARITION"}
                            </Badge>
                            <span className="text-xs text-slate-400">{formatTimeAgo(alert.created_at)}</span>
                          </div>
                          <p className="text-sm font-medium text-slate-700 mt-1">
                            {alert.location_name || alert.address || "Position signalée"}
                          </p>
                          {alert.description && (
                            <p className="text-xs text-slate-500 mt-1">{alert.description}</p>
                          )}
                          {alert.physical_description && (
                            <p className="text-xs text-slate-600 mt-1 italic">"{alert.physical_description}"</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => reportAlert(alert.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title="Signaler comme fausse alerte"
                      >
                        <Flag size={16} />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Legal Notice */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertTriangle className="text-slate-500 flex-shrink-0" size={20} />
              <div className="text-xs text-slate-600">
                <p className="font-bold mb-1">AVERTISSEMENT LÉGAL</p>
                <p>
                  Le terme "Alerte Enlèvement" est un dispositif officiel du Ministère de la Justice. 
                  SHARE utilise "Alerte Disparition Inquiétante" pour les signalements communautaires.
                </p>
                <p className="mt-2 font-medium">
                  En cas d'enlèvement avéré ou danger immédiat : appelez le <strong>17</strong> (Police), 
                  <strong>112</strong> (Urgences) ou <strong>116 000</strong> (Enfants disparus).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Creation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className={`p-4 border-b flex items-center justify-between ${
              showModal === "vol" ? "bg-red-50" : "bg-orange-50"
            }`}>
              <div className="flex items-center gap-3">
                {showModal === "vol" ? (
                  <ShieldAlert className="text-red-600" size={24} />
                ) : (
                  <UserX className="text-orange-600" size={24} />
                )}
                <h2 className="font-bold text-lg">
                  {showModal === "vol" ? "Signaler un VOL" : "Signaler une DISPARITION"}
                </h2>
              </div>
              <button onClick={() => setShowModal(null)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Step 1: Legal warning for disparition */}
              {showModal === "disparition" && step === 1 && (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex gap-3">
                      <AlertTriangle className="text-amber-600 flex-shrink-0" size={24} />
                      <div className="text-sm">
                        <p className="font-bold text-amber-800 mb-2">IMPORTANT</p>
                        <p className="text-amber-700">
                          Ce système est réservé aux disparitions inquiétantes de personnes vulnérables 
                          (enfants, personnes âgées).
                        </p>
                        <p className="text-amber-700 mt-2 font-medium">
                          Pour les cas d'enlèvement, contactez immédiatement le <strong>17</strong> (Police) 
                          ou le <strong>112</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    onClick={() => setStep(2)}
                  >
                    J'ai compris, continuer
                  </Button>
                </div>
              )}

              {/* Step 1 for vol, Step 2 for disparition: Type selection */}
              {((showModal === "vol" && step === 1) || (showModal === "disparition" && step === 2)) && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Type de {showModal === "vol" ? "vol" : "personne"}</Label>
                    <div className="grid grid-cols-1 gap-2">
                      {SUBTYPES[showModal].map((subtype) => (
                        <button
                          key={subtype.id}
                          onClick={() => setFormData(prev => ({ ...prev, sub_type: subtype.id }))}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            formData.sub_type === subtype.id
                              ? showModal === "vol" 
                                ? "border-red-500 bg-red-50" 
                                : "border-orange-500 bg-orange-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <span className="mr-2">{subtype.icon}</span>
                          <span className="font-medium">{subtype.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button 
                    className={`w-full ${showModal === "vol" ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600"}`}
                    onClick={() => setStep(showModal === "vol" ? 2 : 3)}
                    disabled={!formData.sub_type}
                  >
                    Continuer <ChevronRight size={18} />
                  </Button>
                </div>
              )}

              {/* Step 2 for vol, Step 3 for disparition: Location */}
              {((showModal === "vol" && step === 2) || (showModal === "disparition" && step === 3)) && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Localisation de l'incident</Label>
                    <Button
                      variant="outline"
                      className="w-full mb-2"
                      onClick={getLocation}
                      disabled={locating}
                    >
                      {locating ? (
                        <Loader2 className="animate-spin mr-2" size={18} />
                      ) : (
                        <Locate size={18} className="mr-2" />
                      )}
                      {locating ? "Localisation..." : "Utiliser ma position GPS"}
                    </Button>
                    <div className="text-center text-sm text-slate-400 my-2">ou</div>
                    <Input
                      placeholder="Entrez une adresse..."
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    />
                    {formData.latitude && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-green-600">
                        <CheckCircle size={16} />
                        <span>Position détectée</span>
                      </div>
                    )}
                  </div>
                  <Button 
                    className={`w-full ${showModal === "vol" ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600"}`}
                    onClick={() => setStep(showModal === "vol" ? 3 : 4)}
                    disabled={!formData.latitude && !formData.address}
                  >
                    Continuer <ChevronRight size={18} />
                  </Button>
                </div>
              )}

              {/* Step 3 for vol, Step 4 for disparition: Description */}
              {((showModal === "vol" && step === 3) || (showModal === "disparition" && step === 4)) && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Description (optionnel, 150 car. max)
                    </Label>
                    <Input
                      placeholder="Décrivez brièvement la situation..."
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        description: e.target.value.slice(0, 150) 
                      }))}
                      maxLength={150}
                    />
                    <p className="text-xs text-slate-400 mt-1">{formData.description.length}/150</p>
                  </div>
                  
                  {showModal === "disparition" && (
                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Description physique (obligatoire)
                      </Label>
                      <Input
                        placeholder="Ex: garçon 8 ans, cheveux bruns, t-shirt rouge..."
                        value={formData.physical_description}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          physical_description: e.target.value.slice(0, 150) 
                        }))}
                        maxLength={150}
                      />
                      <p className="text-xs text-slate-400 mt-1">{formData.physical_description.length}/150</p>
                    </div>
                  )}

                  <Button 
                    className={`w-full ${showModal === "vol" ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600"}`}
                    onClick={() => setStep(showModal === "vol" ? 4 : 5)}
                    disabled={showModal === "disparition" && !formData.physical_description}
                  >
                    Continuer <ChevronRight size={18} />
                  </Button>
                </div>
              )}

              {/* Final Step: Slide to confirm */}
              {((showModal === "vol" && step === 4) || (showModal === "disparition" && step === 5)) && (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                    <h3 className="font-bold">Récapitulatif</h3>
                    <p className="text-sm"><strong>Type:</strong> {SUBTYPES[showModal].find(s => s.id === formData.sub_type)?.label}</p>
                    <p className="text-sm"><strong>Lieu:</strong> {formData.address || "Position GPS"}</p>
                    {formData.description && (
                      <p className="text-sm"><strong>Description:</strong> {formData.description}</p>
                    )}
                    {formData.physical_description && (
                      <p className="text-sm"><strong>Physique:</strong> {formData.physical_description}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <p className="text-center text-sm text-slate-500">Glissez pour confirmer l'alerte</p>
                    <div 
                      className={`relative h-14 rounded-full overflow-hidden ${
                        showModal === "vol" ? "bg-red-100" : "bg-orange-100"
                      }`}
                      onMouseMove={(e) => isDragging && handleSlideConfirm(e)}
                      onMouseUp={() => { setIsDragging(false); setSlideProgress(0); }}
                      onMouseLeave={() => { setIsDragging(false); setSlideProgress(0); }}
                      onTouchMove={(e) => handleSlideConfirm(e)}
                      onTouchEnd={() => setSlideProgress(0)}
                    >
                      <div 
                        className={`absolute inset-y-0 left-0 ${
                          showModal === "vol" ? "bg-red-500" : "bg-orange-500"
                        } transition-all`}
                        style={{ width: `${slideProgress * 100}%` }}
                      />
                      <div 
                        className={`absolute inset-y-1 left-1 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center cursor-grab active:cursor-grabbing transition-transform ${
                          loading ? "animate-pulse" : ""
                        }`}
                        style={{ transform: `translateX(${slideProgress * (100 - 56)}px)` }}
                        onMouseDown={() => setIsDragging(true)}
                        onTouchStart={() => setIsDragging(true)}
                      >
                        {loading ? (
                          <Loader2 className="animate-spin text-slate-400" size={24} />
                        ) : (
                          <Send className={showModal === "vol" ? "text-red-500" : "text-orange-500"} size={20} />
                        )}
                      </div>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-medium text-slate-500 pointer-events-none">
                        {slideProgress > 0.5 ? "Relâchez pour envoyer" : "→ Glissez pour alerter →"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
                    <Bell size={14} />
                    <span>Notification push + SMS envoyés dans un rayon de 5km</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UrgentAlertsPage;
